import {html} from 'lit-html';
import {repeat} from 'lit-html/directives/repeat';

import {formatMoney} from '../money';

import Util from '../util';
import {currencyConvert, ThemeConfig, ThemeCurrency} from '../const';
import {cdnImg} from '../shopify-cdn';
import Translate from '../translate';

import FontLoader from '../font-loader';
import * as log from 'loglevel';

// update text help manually to updated params before the full render
export const textHelpUpdate = (e, option) => {
    let textEl = e.target;
    if(textEl){
        // find text help element in option // to support more option types:  Util.findParent(target, '.cl-po--option').querySelector('.cl-po--text-help');
        let textHelpEl = textEl.parentNode.querySelector('.cl-po--text-help');
        // To use with normal help: let textHelpEl = Util.findParent(textEl, '.cl-po--option').querySelector('.cl-po--help');
        if(textHelpEl){
            // sync value to update used length
            option.value = textEl.value;
            // update html body
            textHelpEl.innerHTML = textHelpRender(option.params.textHelp, option); // for normal help:  option.params.help
        }
    }
}

export const textHelpRender = (textHelp, option) => {
    if(textHelp){
        let params = {};

        params.used = option.value.length;
        params.min = option.params.min ? option.params.min : 0;
        params.max = option.params.max ? option.params.max : 1000;
        params.left = params.max - params.used;

        // replace template code based on Translate
        for(let key in params){
            if(params.hasOwnProperty(key)){
                textHelp = textHelp.replace(new RegExp('{' + key + '}', 'g'), params[key]);
            }
        }

        return textHelp;
    }
}

// json serialized escaped \n fix
export const textNewline = (text) => text ? text.replace(/\\n/g, '\n') : text;

export const stripHtmlText = (text) => text.replace(/(<([^>]+)>)/gi, '');

export const priceTemplate = (price, template, relative) => {
    // only render if prices are visible
    if(ThemeConfig.priceVisible && price){
        // default more efficient template
        let priceText;
        if(!template){
            priceText = `(+ ${price})`;
        } else {
            priceText = template.replace(new RegExp('{price}', 'g'), price);
        }
        // NEW: relative price replace + with minus sign
        if(relative){
            if(priceText.indexOf('-') !== -1){ // contains - sign if price was negative
                priceText = priceText.replace('-', '').replace('+', '-');
            } else if(priceText.indexOf('==') !== -1){ // contains "==" sign if price is the current selected option  "==" required to keep html tags like style="" or class=""
                priceText = priceText.replace('==', '').replace('+', ''); // show no sings
            }
        }
        // NEW: checkout ext negative price (experimental)
        if(ThemeCurrency.checkout.negative){
            if(priceText.indexOf('-') !== -1){ // contains - sign if price was negative
                priceText = priceText.replace('-', '').replace('+', '-');
            }
        }
        return priceText;
    }
    return '';
}

export const getOptionValue = (option) => {

    if(option.values){
        // NEW: check multiSelect flag
        let multiSelect = option._controller && option._controller.multiSelect;
        let texts = multiSelect ? [] : null;

        for (let i = 0; i < option.values.length; i++) {
            const optionValue = option.values[i];
            // multi select: save all value texts
            if(multiSelect && option._controller.isSelected(option, optionValue)){
                texts.push(valueText(optionValue));
            }
            // default single value selection
            else if(optionValue.value == option.value){
                return optionValue;
            }
        }

        // multi select return single optionValue with texts
        if(texts && texts.length){
            return { text: texts.join(',') }
        }
    }

    // NEW: fallback value itself (e.g for color picker)
    if(option.value){
        return { text: option.value }
    }

    return null;
}

export const valueText = (optionValue) => optionValue ? (optionValue.text ? optionValue.text : optionValue.value || '') : '';

export const optionValueText = (option, optionValue, moneyFormat, stripHtml = false) => {
    let text = valueText(optionValue);

    // check if price should be shown in text
    if(option.params.valuePriceText){

        let priceText = optionValuePriceText(option, optionValue, moneyFormat);

        if(priceText){
            text += ' ' + priceText;
        }
    }

    if(stripHtml){
        text = stripHtmlText(text);
    }

    return text;
}

export const optionValuePriceText = (option, optionValue, moneyFormat) => {
    // optional disabled text to show instead of the price (checks if custom translation is found)
    if(optionValue.disabled === true){
        let disableText = Translate.get('product.options.disabled', null, '_');
        if(disableText !== '_'){
            return `(${disableText})`;
        }
    }
    // check if price is set
    if(optionValue.price && moneyFormat){
        return priceTemplate(optionValuePrice(option, optionValue, moneyFormat), option.params.priceTemplate, option.params.priceRelative);
    }
    return undefined; // undefined to exclude from tooltip
}

export const optionValuePrice = (option, optionValue, moneyFormat) => {
    // price is set and money format provided
    if(optionValue.price && moneyFormat){
        let price = optionValue.price;

        // NEW: show price relative to the current option price
        if(option.params.priceRelative){
            for (let i = 0; i < option.values.length; i++) {
                const oValue = option.values[i];
                if(option.value === oValue.value){
                    // calculate difference
                    price = price - oValue.price;
                    // own price is 0
                    if(price === 0){
                        if(option.params.priceRelativeEmpty){
                            return ''; // don't show price of selected option
                        }
                        // reset to show absolute price
                        price = optionValue.price;
                        // hacky solution to add "==" (removes + sign in priceTemplate)
                        moneyFormat = '==' + moneyFormat;
                    }
                    break;
                }
            }
        }

        // NEW: dynamic currency convert support  // exclude priceVariant > already fetched in current currency
        if(ThemeCurrency.currencyConvert && !optionValue.priceVariant){
            price = currencyConvert(price);
        }
        return formatMoney(price * 100, moneyFormat);
    }

    return '';
}

export const imgTooltipValueText = (option, optionValue, moneyFormat, stripHtml = false)  => {

    let text = '';

    if(option.params.imgTooltip){  // INFO: max-width is set in product-options tooltip init  , max-height is set in style.css
        text += `<img src="${cdnImg(optionValue.img, '600x')}" />${text}`;
    }
    // NEW: text below img swatch avoid adding text on tooltip
    if(!option.params.imgText){
        text += optionValueText(option, optionValue, moneyFormat, stripHtml);
    }
    // return undefined if empty (avoids showing tooltip)
    if(text.trim() === ''){
        return undefined;
    }

    return text;
}

export const inputColorSupported = () => {
    // test if supported: https://stackoverflow.com/a/51168345
    let input = document.createElement('input');
    input.type = 'color';
    input.value = '!';
    if(input.type === 'color' && input.value !== '!') {
        // set default value if provided
        return true;
    }
    return false;
}

export const inputDateSupported = () => {
    let input = document.createElement('input');
    let value = 'a';
    input.setAttribute('type', 'date');
    input.setAttribute('value', value);
    return (input.value !== value);
};

export const optionValueClassMap = (option, optionValue, classMap = {}) => {
    // disabled class       || variant option > show as disabled if not available (explicit false check required to avoid not checked showing unavailable)
    if(optionValue.disabled || (option._variantOption && optionValue.available === false)){
        classMap['cl-po--disabled'] = true;
    }
    return classMap;
}

export const optionClassMap = (option, classMap = {}) => {
    classMap['cl-po--invalid'] = option.errors && option.errors.length > 0;

    // collapsed text editor remove border radius (CSS only solution would be nicer but css does not support previous sibling selector)
    if(option._editor && option._layer.params.collapse){
        classMap['cl-po--b-radius-0'] = true;
    }

    /* NOT USED currently but could be useful to add more classes to the option element
    INFO: lit-html class only allows a single binding, we convert this param into the map syntax to apply the class
    if(option.params.className && typeof option.params.className == 'string'){
        let classNameList = option.params.className.split(' ');
        for (let i = 0; i < classNameList.length; i++) {
            classMap[classNameList[i]] = true;
        }
    } */

    return classMap;
}

export const sizeClassMap = (option, defaultSize, classMap = {}) => {
    if(option.params.size){
        // generate class name with size param
        classMap['cl-po--' + option.params.size] = true;
    } else if(defaultSize){
        classMap['cl-po--' + defaultSize] = true;
    }

    // NEW: circle shape
    if(option.params.shape){
        classMap['cl-po--' + option.params.shape] = true;
    }

    return classMap;
}

export const swatchToggle = (e, option, optionValue, onChange) => {
    // if toggle is enabled && the already active swatch is clicked > de-select
    if(option.params.toggle && option.value === optionValue.value){
        // IMPORTANT: cancel event - otherwise browser auto selects radio again (via label click handler trigger > input.checked set)
        e.preventDefault();
        e.stopImmediatePropagation();
        // reset value
        option.value = '';
        onChange(option);
    }
}

export const buttonStyleMap = (option, styleMap = {}) => {
    if(option.params.minWidth && option.params.minWidth > 0){
        styleMap['minWidth'] = option.params.minWidth + 'px';
    }
    return styleMap;
}

export const buttonColorStyleMap = (option, styleMap = {}) => {
    if(option.params.btnColor){
        styleMap['color'] = option.params.btnColor;
    }
    if(option.params.btnBgColor){
        styleMap['backgroundColor'] = option.params.btnBgColor;
    }
    return styleMap;
}

export const optionGroupLayoutStyle = (option) => {
    // :not() excludes a nested group-editor (shares the .cl-po--group class)
    let groupSelector = `[data-option="${option.name}"] .cl-po--group:not(.cl-po--group-editor)`;

    // generate CSS grid layout
    if(option.params.groupLayout === 'grid'){
        let gridCss = `${groupSelector} {
          margin: 0 auto;
          display: grid;
          gap: 1rem;
          grid-template-columns: repeat(${option.params.groupCols || 2}, 1fr);
        }`;

        if(option.params.groupColsM){ // same media query as in style.css
            gridCss += ` @media only screen and (max-width: 450px) { ${groupSelector} {
              grid-template-columns: repeat(${option.params.groupColsM}, 1fr);
            } }`;
        }
        return gridCss;
    }
    return '';
}

// INFO: is called everytime when the event handler is bound -> returns a new handler with debounce functionality
// @input event is triggered on every key
// @blur event is not bound - if the debounce is long the blur event may needs to be added
export const optionDebounce = (option, handler, debounce = 300) => {

    // custom debounce on option
    debounce = option.params.debounce || debounce;

    if(debounce === 0){
        return handler;
    }

    // default debounce changes
    return Util.debounced(debounce, handler);
}

export const optionName = (option) => {
    // explicit name (required for variant options)
    if(option.params.name){
        return option.params.name;
    }
    // NEW: excluded on product properties (useful for product add on only option)
    if(option.params.exclude){
        return '_' + option.name; // option name without properties (not saved)
    }
    // default wrapped in properties
    return `properties[${option.name}]`;
}

export const optionAutocomplete = (option, off = 'noop') => { // INFO: 'off' still triggers auto complete 'noop' works
    // explicit auto complete value: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
    if(option.params.autocomplete){
        return option.params.autocomplete;
    }
    // default off
    return off;
}

export const optionFontFamily = (option, getOption) => {
    // check if a font option is linked
    if(option.params.optionFont){
        // find option via helper function
        let fontOption = getOption(option.params.optionFont);

        if(fontOption && fontOption.value){
            let fontFamily = fontOption.value;
            let font = FontLoader.getFont(fontFamily);
            if (font) {
                if(!font.loaded){  // excluded already loaded
                    // we can ignore promise - browser auto updates the font when it is loaded)
                    FontLoader.loadFontsByName([ fontFamily ])
                }
                return fontFamily;
            }
        }
    }
    // don't add the font style
    return null;
}

export const textValueSync = (e, option, onChange) => {

    // special text modification support
    if(e.target.value){

        // NEW: errors to support real time validation
        let errors = [];

        // CSS & actual transform the input (to render on preview)
        if(option.params.textTransform === 'uppercase'){
            e.target.value = e.target.value.toUpperCase();
        } else if(option.params.textTransform === 'lowercase'){
            e.target.value = e.target.value.toLowerCase();
        }
        // max input rows (for textarea)
        if(option.params.rowsMax){
            let rows = e.target.value.split('\n');
            if(rows.length > option.params.rowsMax){
                rows.length = option.params.rowsMax; // set new array size - this removes all elements after the index
                e.target.value = rows.join('\n'); // update value only with the allowed rows
            }
        }
        // maxlength="" is not validate on all devices on input (e.g Android allows more inputs)
        if(option.params.max && e.target.value.length > option.params.max){
            e.target.value = e.target.value.substring(0, option.params.max);
        }
        // NEW: global text clean setting <script>window.clTextClean = '\\p{Extended_Pictographic}|\\p{Regional_Indicator}|[\\u{200D}\\u{FE0F}]';</script>
        if(window.clTextClean){
            option.params.textClean = window.clTextClean; // use global setting
        }
        if(window.clTextCleanFlags){
            option.params.textCleanFlags = window.clTextCleanFlags; // use global setting
        }
        // remove all characters that match the provided regex
        if(option.params.textClean){

            // create regex if not already
            if(!option._textClean){
                try {
                    option._textClean = new RegExp(option.params.textClean, option.params.textCleanFlags || 'ugi'); // default flags: always use unicode and global flags & case insensitive
                } catch (error){
                    log.error('failed to compile textClean pattern for option: ', option.params.textClean, option, error);
                }
            }

            if(option._textClean){
                // check if text contains pattern to clean
                if(option._textClean.test(e.target.value)){
                    e.target.value = e.target.value.replaceAll(option._textClean, '');
                    // support clean specific translated message, fallback to general validation message (already translated), fallback to default message
                    errors.push(Translate.get('product.options.validity.textClean', null, option.params.validationMsg || 'Input not allowed'));
                }
            }
        }

        if(errors.length){
            option.errors = errors;
            option._error = true; // flag to avoid error rest on change
        }
    }

    return valueSync(e, option, onChange);
}

export const valueSync = (e, option, onChange) => {
    // basic option value sync
    option.value = e.target.value;
    return onChange(option);
}

export const textEditorProperty = (option) => {
    let settings = [];
    if(option._layer.params.editor.font) {
        settings.push('font:' + (option._editor.fontFamily || ''));
    }
    if(option._layer.params.editor.style){
        settings.push('weight:' + (option._editor.fontWeight || 'normal'));
        settings.push('style:' + (option._editor.fontStyle || 'normal'));
    }
    if(option._layer.params.editor.align){
        settings.push('align:' + (option._editor.textAlign || 'left'));
    }
    if(option._layer.params.editor.size){
        settings.push('size:' + (option._editor.fontSize || 'normal'));
    }
    if(option._layer.params.editor.color){
        settings.push('color:' + (option._editor.color || '#000000'));
    }

    return settings.join(',')
}

export const parseTextEditorProperty = (value) => {
    let patch = {};

    if (!value || typeof value !== 'string') {
        return patch;
    }

    const propertyMap = {
        font: 'fontFamily',
        weight: 'fontWeight',
        style: 'fontStyle',
        align: 'textAlign',
        size: 'fontSize',
        color: 'color'
    };

    const values = value.split(',');

    for (let i = 0; i < values.length; i++) {
        const row = values[i];
        const separatorIndex = row.indexOf(':');

        if (separatorIndex === -1) {
            continue;
        }

        const key = row.substring(0, separatorIndex).trim();
        const targetKey = propertyMap[key];

        if (!targetKey) {
            continue;
        }

        const rawValue = row.substring(separatorIndex + 1).trim();

        if (!rawValue) {
            continue;
        }

        if (targetKey === 'fontSize') {
            const fontSize = parseFloat(rawValue);

            if (!isNaN(fontSize) && isFinite(fontSize)) {
                patch.fontSize = fontSize;
            }

            continue;
        }

        patch[targetKey] = rawValue;
    }

    return patch;
}

// groups option values based on optionValue.text prefix
export const optionValuesGroupBy = (values, delimiter) => {

    let groupValues = [];

    let currentGroup= null;

    const groupMap = {}; // map to find existing groups (allows to add new values to previous groups)

    for (let i = 0; i < values.length; i++) {
        const optionValue = values[i];
        //                      _text contains the original value
        const groupSplit = (optionValue._text || optionValue.text || '').split(delimiter);
        if(groupSplit.length > 1){
            const groupLabel = groupSplit[0];
            if(!currentGroup || groupLabel !== currentGroup.groupLabel){
                // check if existing group
                if(groupMap.hasOwnProperty(groupLabel)){
                    currentGroup = groupMap[groupLabel];
                } else {
                    currentGroup = {
                        groupLabel: groupLabel,
                        values: []
                    };
                    groupValues.push(currentGroup);
                    groupMap[groupLabel] = currentGroup;
                }
            }
            // only set the text without the group label
            if(!optionValue._text){
                optionValue._text = optionValue.text; // preserve original text (required to re-execute grouping)
                optionValue.text = groupSplit[1];
            }

            currentGroup.values.push(optionValue);

        } else {
            // add optionValue without group
            groupValues.push(optionValue);
        }
    }

    return groupValues;
}

export const controllerMultiSelect = (option, config, onChange) => {

    return {
        multiSelect: true, // flag for price options to support multiple values
        onValidate: (option) => {

            // new min/max selections validations
            let count = option.value ? option.value.split(',').length : 0;

            if(option.params.minSelect && count < option.params.minSelect){
                option.errors.push(Translate.get('product.options.validity.selectMin', { min: option.params.minSelect }));
                return false;
            }
            if(option.params.maxSelect && count > option.params.maxSelect){
                option.errors.push(Translate.get('product.options.validity.selectMax', { max: option.params.maxSelect }));
                return false;
            }

            // custom validation make sure at least one value is set
            if(option.required && !option.value){
                option.errors.push(Translate.get('product.options.validity.valueMissing'));
                return false;
            }

            return true;
        },
        onChange: (e, option) => { // IMPORTANT: to pass option and not use the reference of the ['checkbox'] = (option, ..)   in editorMode the option reference can change

            // input with the combined values
            let checkbox = document.querySelector('#' + option.id);

            if(checkbox){
                // find all individual checkboxes  NEW: - suffix to avoid conflicts with other options e.g option-1 and option-11
                let checkboxOptions = document.querySelectorAll('[id^="'+option.id+'-"]');
                let values = [];
                for (let i = 0; i < checkboxOptions.length; i++) {
                    const checkboxOption = checkboxOptions[i];
                    // exclude the combined option
                    if(checkboxOption !== checkbox && checkboxOption.checked){
                        values.push(checkboxOption.value);
                    }
                }

                // join values to single value
                let value = values.join(',');
                checkbox.value = value;
                // syn with option model
                option.value = value;
                // trigger change
                onChange(option);
            }

        },
        isSelected: (option, optionValue) => {
            // value check with multi select substring support
            // equal check same as in product-options.js
            if(String(optionValue.value).trim() === String(option.value).trim()){
                return true;
            }
            // value contains check
            if(option.value && option.value.indexOf(optionValue.value) !== -1){
                // NEW: we also have to split to check for the exact value match
                let values = option.value.split(',');
                if(values.indexOf(optionValue.value) !== -1){
                    return true;
                }
            }

            return false;
        }
    };
}

export const optionQuantityInput = (option, optionValue, onChange) => {
    // check if quantityInput is enabled
    if(optionValue.quantityInput && optionValue.priceVariant){
        return html`
            <div class="cl-po--quantity-input">
                <input type="number" class="cl-po--input" min="1" step="1" max="999" .value=${optionValue.priceVariant.quantity} @input=${optionDebounce(option, (e) => { optionValue.priceVariant.quantity = parseInt(e.target.value); onChange(option); })}>
            </div>
        `;
    }
    return null;
}

export const textEditorCollapse = (option) => html`
    ${option._layer.params.collapseLabel ?  html`<span class="cl-po--edit-label">${option._layer.params.collapseLabel}</span>` : null}
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20"><path d="M15.728 9.686l-1.414-1.414L5 17.586V19h1.414l9.314-9.314zm1.414-1.414l1.414-1.414-1.414-1.414-1.414 1.414 1.414 1.414zM7.242 21H3v-4.243L16.435 3.322a1 1 0 0 1 1.414 0l2.829 2.829a1 1 0 0 1 0 1.414L7.243 21z" fill="var(--text-editor-color)"/></svg>
`;

export const collapseToggle = (e) => {
    let accordionEl = e.currentTarget;

    let open = accordionEl.className.indexOf('open') !== -1;

    let panel = accordionEl.nextElementSibling;

    if (!open) {
        panel.style.maxHeight = panel.scrollHeight + 100 + 'px';
        // NEW: after css transition of max-height (only used for animation)  reset max-height to 100% to support growing content of group)
        setTimeout(() => {
            if(panel.style.maxHeight){ // prevent already closed
                panel.style.maxHeight = null; // NEW: reset to use max height from style 100%
            }
        }, 500); // animation is 200ms
    }

    // toggle class for animation
    accordionEl.classList.toggle('cl-po--open');
}

export const collapse = (title, content, open, classList = '') => html`
    <div class="cl-po--collapse ${open ? classList + ' cl-po--open' : classList}" @click=${(e) => collapseToggle(e)}>${title}</div>
    <div class="cl-po--panel ${classList}">
        ${content}
    </div>
`;

// renders swatch values with optional group labels when groupDelimiter is set
// valueTemplate: (optionValue, index) => html`...` - renders a single swatch item
// breakTemplate: (optionValue, index) => html`...` | null - optional break logic (e.g. items param)
export const swatchGroupTemplate = (option, valueTemplate, breakTemplate) => {
    if(!option.params.groupDelimiter){
        return null; // caller should use default rendering
    }

    let groupValues = optionValuesGroupBy(option.values, option.params.groupDelimiter);

    let globalIndex = 0; // track index across groups for break logic

    return html`${repeat(groupValues, (group) => group.groupLabel || group.value, (group) => {
        if(group.groupLabel){
            return html`
                <div class="cl-po--swatch-group">
                    <div class="cl-po--swatch-group-label">${group.groupLabel}</div>
                    <div class="cl-po--swatch-group-items">
                        ${repeat(group.values, (ov) => ov.value, (ov) => {
                            let idx = globalIndex++;
                            return html`${valueTemplate(ov, idx)}${breakTemplate ? breakTemplate(ov, idx) : null}`;
                        })}
                    </div>
                </div>
            `;
        }
        // ungrouped value
        let idx = globalIndex++;
        return html`${valueTemplate(group, idx)}${breakTemplate ? breakTemplate(group, idx) : null}`;
    })}`;
}
